// @ts-check

/**
 * @file Renders the project kanban board screen.
 */

import {useIsFocused} from "@react-navigation/native"
import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useMemo} from "react"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {useWindowDimensions} from "react-native"
import {ActivityIndicator, ScrollView, Text, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import useNow from "set-state-compare/build/use-now.js"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"
import RemoteBoardColumn from "../../../../src/frontend-models/board-column"
import {Comment as RemoteComment} from "../../../../src/frontend-models/comment"
import RemoteTask from "../../../../src/frontend-models/task"
import reorderBoardColumns from "../../../../src/frontend-model-commands/board-columns/reorder"
import reorderTaskBoards from "../../../../src/frontend-model-commands/task-boards/reorder"
import errorMessage from "../../../../src/shared/error-message"
import {optionalRouteParam, requiredRouteParam} from "../../../../src/shared/route-params"
import {createLocalBoardData, fullRowNumberForVisibleInsertion, LocalTaskBoardReadView, localBoardColumns, localTaskBoardForTaskId, localTaskBoardsForColumn} from "../../../../src/screens/projects/board/local-board-data"
import {projectBoardControlsAvailable} from "../../../../src/screens/projects/board/project-board-controls-availability"
import ProjectBoardColumnCard, {PROJECT_BOARD_COLUMN_CARD_MARGIN_RIGHT, PROJECT_BOARD_COLUMN_CARD_WIDTH} from "../../../../src/screens/projects/board/board-column-card"
import ProjectBoardColumnMenu from "../../../../src/screens/projects/board/column-menu"
import ProjectBoardDeleteConfirmModal from "../../../../src/screens/projects/board/delete-confirm-modal"
import ProjectBoardFilters from "../../../../src/screens/projects/board/board-filters"
import {taskStateOptions} from "../../../../src/shared/task-state"
import ProjectBoardNewColumnModal from "../../../../src/screens/projects/board/new-column-modal"
import ProjectBoardNewTaskModal from "../../../../src/screens/projects/board/new-task-modal"
import ProjectBoardTaskMenu from "../../../../src/screens/projects/board/task-menu"
import {BOARD_LAYOUT_SIDEBAR_WIDTH} from "../../../../src/components/board-layout"
import buildRoutePath from "../../../../src/shared/build-route-path"
import ScreenTitle from "../../../../src/components/screen-title"
import TaskDetailModal from "../../../../src/components/tasks/detail-modal"
import {pathForQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import useThemeColors, {useThemeName} from "../../../../src/shared/use-theme-colors"
import useCurrentUser from "../../../../src/auth/use-current-user"
import {projectReplicaRowsSource} from "../../../../src/database/project-replica-reads"
import LocalBoardColumn from "../../../../src/models/board-column"
import LocalBoard from "../../../../src/models/board"
import LocalProject from "../../../../src/models/project"
import LocalTaskAssignedUser from "../../../../src/models/task-assigned-user"
import LocalTaskBoard from "../../../../src/models/task-board"
import LocalTaskType from "../../../../src/models/task-type"
import LocalTask from "../../../../src/models/task"
import LocalUser from "../../../../src/models/user"
import ProjectSyncStatusIndicator from "../../../../src/components/project-sync-status-indicator.jsx"
import {useProjectSyncOnline} from "../../../../src/sync/project-sync-connectivity"
import {projectSyncLifecycle, useActiveProjectReplica} from "../../../../src/sync/project-sync-lifecycle"
import {useProjectSyncStatus} from "../../../../src/sync/project-sync-status.js"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {InstanceType<typeof LocalBoardColumn>} BoardColumn */
/** @typedef {InstanceType<typeof LocalTask>} Task */
/** @typedef {import("../../../../src/screens/projects/board/local-board-data").LocalTaskBoardReadView} TaskBoard */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @type {Record<string, object>} */
const styles = {}
// Same-route replacement can briefly remount with the just-dismissed task_id.
/** @type {Record<string, string>} */
const dismissedRouteSelectedTaskIds = {}
const PROJECT_BOARD_QUERY_PARAM_SELECTOR = ["filter", "states", "search", "task_id"]
/** @type {string[]} */
const ALL_TASK_STATES = taskStateOptions().map((stateOption) => stateOption.value)

/** @type {Set<string>} */
const ALL_TASK_STATE_SET = new Set(ALL_TASK_STATES)

/** @type {string[]} */
const DEFAULT_VISIBLE_TASK_STATES = ALL_TASK_STATES.filter((state) => state !== "closed")

/** @type {string} */
const DEFAULT_VISIBLE_TASK_STATES_KEY = DEFAULT_VISIBLE_TASK_STATES.join(",")

/**
 * @typedef {"all" | "due_soon" | "mine"} BoardFilterKey
 */

/**
 * @typedef {{columnId?: string, taskBoardId?: string, type: "column" | "task"} | null} BoardDeleteTarget
 */

/** @typedef {{column: BoardColumn, previewRowNumber: number, rowNumber: number}} TaskBoardDropTarget */

/** @typedef {{height: number, width: number, x: number, y: number}} BoardItemLayout */

/** @typedef {{x: number, y: number}} BoardDragPosition */

/** @typedef {{rowNumber: number, sourceColumnId: string, targetColumnId: string, taskBoardId: string}} TaskBoardDragPreview */

/** @typedef {{columnId: string, sourcePosition: number, targetPosition: number}} BoardColumnDragPreview */

/** @typedef {{[key: string]: unknown}} ProjectBoardRouteProps */

/**
 * @typedef {object} ProjectBoardRouteState
 * @property {boolean} approvingAutomationPlan Whether a selected-task approval comment is being created.
 * @property {BoardFilterKey} activeFilter Active board filter.
 * @property {ReturnType<typeof createLocalBoardData> | null} boardData Loaded board data for the route.
 * @property {number} boardRevision Revision counter used to refresh rendered board columns.
 * @property {BoardColumnDragPreview | null} columnDragPreview Current drag target used for live column spacing.
 * @property {BoardDeleteTarget} confirmingDelete Delete target pending confirmation.
 * @property {string | null} currentUserId Current user id used for assignment filters and new tasks.
 * @property {TaskBoardDragPreview | null} dragPreview Current drag target used for live task-card spacing.
 * @property {string | null} editingColumnId Column id currently open in the column modal.
 * @property {string | null} editingTaskBoardId Task-board id currently open in the task modal.
 * @property {boolean} loading Whether board data is loading.
 * @property {boolean} notFound Whether the route did not resolve to a board.
 * @property {string} searchQuery Search query shown in the board filters.
 * @property {Task | null} selectedTaskDetails Fully loaded details for the selected task.
 * @property {TaskBoard | null} selectedTaskBoardDetails Tenant-local placement retained independently of card filters.
 * @property {string} selectedTaskId Task id currently shown in the detail modal.
 * @property {string[]} visibleTaskStates Task states visible on the board.
 * @property {string | null} showColumnMenuForColumnId Column id whose menu is visible.
 * @property {boolean} showNewColumnModal Whether the new-column modal is visible.
 * @property {string | null} showNewTaskModalForColumnId Column id receiving a new task.
 * @property {boolean} showTaskStatesModal Whether the task-state filter modal is visible.
 * @property {string | null} showTaskMenuForTaskBoardId Task-board id whose menu is visible.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown below the board.
 * @property {{defaultForProject: boolean, id: string, name: string}[]} taskTypeOptions Task type choices for task modals.
 */

/**
 * Renders a project board with filters, task cards, and edit modals.
 * @augments {ShapeComponent<ProjectBoardRouteProps, ProjectBoardRouteState>}
 */
class ProjectBoardRoute extends ShapeComponent {
  /** @type {string} */
  boardID

  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint

  /** @type {() => RouteQueryParams} */
  currentQueryParams

  /** @type {string | null} */
  currentUserId = null

  /** @type {Record<string, InstanceType<typeof LocalUser>[]>} */
  assignedUsersByTaskId = {}

  /** @type {Set<string>} */
  visibleTaskIds = new Set()

  online = true

  projectName = ""

  /** @type {InstanceType<typeof LocalProject> | null} */
  project = null

  /** @type {boolean} */
  projectSyncUnavailable = false

  /** @type {boolean} */
  queryFailed = false

  /** @type {RouteQueryParams} */
  params

  /** @type {string} */
  projectID

  /** @type {BoardFilterKey} */
  routeActiveFilter

  /** @type {string} */
  routeSearchQuery

  /** @type {string[]} */
  routeVisibleTaskStates

  /** @type {ReturnType<typeof useRouter>} */
  router

  /** @type {ReturnType<typeof useWindowDimensions>} */
  windowDimensions

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {string} */
  routeSelectedTaskID

  /** @type {Map<string, BoardItemLayout>} */
  boardColumnLayouts = new Map()

  /** @type {Map<string, BoardDragPosition>} */
  columnDragStartCenters = new Map()

  /** @type {Map<string, BoardDragPosition>} */
  dragStartCenters = new Map()

  /** @type {boolean} */
  routeFocused = false

  /** @type {BoardFilterKey} */
  lastRouteActiveFilter = "all"

  /** @type {string} */
  lastRouteSearchQuery = ""

  /** @type {string} */
  lastRouteSearchContextKey = ""

  /** @type {string} */
  lastRouteVisibleTaskStatesKey = DEFAULT_VISIBLE_TASK_STATES_KEY

  /** @type {string} */
  lastRouteSelectedTaskId = ""

  /** @type {string} */
  lastSelectedTaskPlacementContextKey = ""

  selectedTaskPlacementObserved = false

  /** @type {Map<string, BoardItemLayout>} */
  taskBoardLayouts = new Map()

  /** @type {ProjectBoardRouteState} */
  state = {
    approvingAutomationPlan: false,
    activeFilter: "all",
    boardData: /** @type {ReturnType<typeof createLocalBoardData> | null} */ null,
    boardRevision: 0,
    columnDragPreview: /** @type {BoardColumnDragPreview | null} */ null,
    confirmingDelete: /** @type {BoardDeleteTarget} */ null,
    currentUserId: /** @type {string | null} */ null,
    dragPreview: /** @type {TaskBoardDragPreview | null} */ null,
    editingColumnId: /** @type {string | null} */ null,
    editingTaskBoardId: /** @type {string | null} */ null,
    loading: true,
    notFound: false,
    searchQuery: "",
    selectedTaskBoardDetails: /** @type {TaskBoard | null} */ null,
    selectedTaskDetails: /** @type {Task | null} */ null,
    selectedTaskId: "",
    visibleTaskStates: DEFAULT_VISIBLE_TASK_STATES,
    showColumnMenuForColumnId: /** @type {string | null} */ null,
    showNewColumnModal: false,
    showNewTaskModalForColumnId: /** @type {string | null} */ null,
    showTaskStatesModal: false,
    showTaskMenuForTaskBoardId: /** @type {string | null} */ null,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    taskTypeOptions: /** @type {{defaultForProject: boolean, id: string, name: string}[]} */ []
  }

  /**
   * Initializes route state and board loading effects.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const routeParams = useLocalSearchParams()
    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(PROJECT_BOARD_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const boardID = requiredRouteParam(routeParams, "boardId", "Board ID")
    const projectID = requiredRouteParam(routeParams, "projectId", "Project ID")
    const replica = useActiveProjectReplica(projectID)
    const projectSyncStatus = useProjectSyncStatus()
    const routeFocused = useIsFocused()
    const {user: currentUser} = useCurrentUser()
    const currentUserId = /** @type {string | null} */ (currentUser ? String(currentUser.id) : null)
    const routeActiveFilter = normalizedFilterValue(optionalRouteParam(params, "filter", "Filter"))
    const routeVisibleTaskStates = visibleTaskStatesFromStatesParam(optionalRouteParam(params, "states", "Task states"))
    const routeVisibleTaskStatesKey = routeVisibleTaskStates.join(",")
    const routeSearchQuery = optionalRouteParam(params, "search", "Search") ?? ""
    const routeSearchContextKey = `${projectID}:${boardID}`
    const boardRouteKey = routeSearchContextKey
    const routeSelectedTaskIDParam = optionalRouteParam(params, "task_id", "Task ID") ?? ""
    const dismissedRouteSelectedTaskId = dismissedRouteSelectedTaskIds[boardRouteKey]
    if (
      !routeSelectedTaskIDParam ||
      (dismissedRouteSelectedTaskId && dismissedRouteSelectedTaskId !== routeSelectedTaskIDParam)
    ) {
      delete dismissedRouteSelectedTaskIds[boardRouteKey]
    }
    const routeSelectedTaskID = dismissedRouteSelectedTaskIds[boardRouteKey] === routeSelectedTaskIDParam
      ? ""
      : routeSelectedTaskIDParam
    const activeFilter = this.s.activeFilter
    const searchQuery = this.s.searchQuery
    const visibleTaskStatesKey = this.s.visibleTaskStates.join(",")
    const boardSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalBoard,
      projectId: projectID,
      query: (boards) => boards.where({id: boardID, projectId: projectID}).limit(1),
      replica
    }), [boardID, projectID, replica])
    const columnsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalBoardColumn,
      projectId: projectID,
      query: (columns) => columns.ransack({boardIdEq: boardID, s: "position asc, id asc"}),
      replica
    }), [boardID, projectID, replica])
    const taskBoardsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTaskBoard,
      projectId: projectID,
      query: (taskBoards) => taskBoards
        .ransack({boardIdEq: boardID})
        .order({column: "board_column_id", direction: "ASC"})
        .order({column: "row_number", direction: "ASC"})
        .order({column: "id", direction: "ASC"}),
      replica
    }), [boardID, projectID, replica])
    const taskTypesSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTaskType,
      projectId: projectID,
      query: (taskTypes) => taskTypes.ransack({projectIdEq: projectID, s: "name asc, id asc"}),
      replica
    }), [projectID, replica])
    const boardState = useLiveQuery(boardSource)
    const columnsState = useLiveQuery(columnsSource)
    const taskBoardsState = useLiveQuery(taskBoardsSource)
    const taskTypesState = useLiveQuery(taskTypesSource)
    const placedTaskIdsKey = [...new Set(taskBoardsState.results.map((taskBoard) => taskBoard.taskId()).filter(Boolean))].sort().join(",")
    const assignmentsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTaskAssignedUser,
      projectId: projectID,
      query: (assignments) => placedTaskIdsKey
        ? assignments.where({taskId: placedTaskIdsKey.split(",")})
        : assignments.where({id: []}),
      replica
    }), [placedTaskIdsKey, projectID, replica])
    const assignmentsState = useLiveQuery(assignmentsSource)
    const mineTaskIdsKey = currentUserId
      ? assignmentsState.results
        .filter((assignment) => assignment.userId() === currentUserId)
        .map((assignment) => assignment.taskId())
        .filter(Boolean)
        .sort()
        .join(",")
      : ""
    const allTasksSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTask,
      projectId: projectID,
      query: (tasks) => placedTaskIdsKey
        ? tasks.where({id: placedTaskIdsKey.split(",")}).order({column: "id", direction: "ASC"})
        : tasks.where({id: []}),
      replica
    }), [placedTaskIdsKey, projectID, replica])
    const visibleTasksSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTask,
      projectId: projectID,
      query: (tasks) => {
        if (!placedTaskIdsKey || (activeFilter === "mine" && !mineTaskIdsKey)) return tasks.where({id: []})
        /** @type {Record<string, unknown>} */
        const filters = {
          idIn: activeFilter === "mine" ? mineTaskIdsKey.split(",") : placedTaskIdsKey.split(","),
          s: "id asc",
          stateIn: visibleTaskStatesKey ? visibleTaskStatesKey.split(",") : ALL_TASK_STATES
        }
        const search = searchQuery.trim()
        if (search) filters.nameCont = search
        return tasks.ransack(filters)
      },
      replica
    }), [activeFilter, mineTaskIdsKey, placedTaskIdsKey, projectID, replica, searchQuery, visibleTaskStatesKey])
    const selectedTaskSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTask,
      projectId: projectID,
      query: (tasks) => routeSelectedTaskID
        ? tasks.where({id: routeSelectedTaskID}).limit(1)
        : tasks.where({id: []}),
      replica
    }), [projectID, replica, routeSelectedTaskID])
    const allTasksState = useLiveQuery(allTasksSource)
    const visibleTasksState = useLiveQuery(visibleTasksSource)
    const selectedTaskState = useLiveQuery(selectedTaskSource)
    const taskBoards = taskBoardsState.results
    const assignments = assignmentsState.results
    const visibleTaskIdsKey = visibleTasksState.results.map((task) => task.id()).sort().join(",")
    const visibleTaskIds = useMemo(() => new Set(visibleTaskIdsKey ? visibleTaskIdsKey.split(",") : []), [visibleTaskIdsKey])
    const visibleAssignments = useMemo(() => {
      const visibleTaskIds = new Set(visibleTaskIdsKey ? visibleTaskIdsKey.split(",") : [])
      return assignments.filter((assignment) => visibleTaskIds.has(String(assignment.taskId())))
    }, [assignments, visibleTaskIdsKey])
    const userIdsKey = [...new Set(visibleAssignments.map((assignment) => assignment.userId()).filter(Boolean))].sort().join(",")
    const usersQuery = useMemo(() => userIdsKey ? LocalUser.where({id: userIdsKey.split(",")}) : LocalUser.where({id: []}), [userIdsKey])
    const projectQuery = useMemo(() => LocalProject.where({id: projectID}).limit(1), [projectID])
    const usersState = useLiveQuery(usersQuery)
    const projectState = useLiveQuery(projectQuery)
    const board = boardState.results[0] || null
    const columns = columnsState.results
    const tasks = allTasksState.results
    const boardData = useMemo(
      () => board ? createLocalBoardData({board, columns, taskBoards, tasks}) : null,
      [board, columns, taskBoards, tasks]
    )
    const queryStates = [boardState, columnsState, taskBoardsState, taskTypesState, assignmentsState, allTasksState, visibleTasksState, selectedTaskState, usersState, projectState]
    const readError = queryStates.find((state) => state.error)?.error || null
    const queryLoading = queryStates.some((state) => state.loading)
    const projectSyncUnavailable = !replica && (projectSyncStatus.state === "failed" || projectSyncStatus.state === "offline")
    const loading = !readError && !projectSyncUnavailable && (!replica || (!this.s.boardData && queryLoading))
    const selectedTask = selectedTaskState.results[0] || null
    const selectedTaskPlacement = selectedTask
      ? taskBoards.find((taskBoard) => String(taskBoard.taskId()) === selectedTask.id()) || null
      : null
    const selectedTaskBoard = useMemo(
      () => selectedTask && selectedTaskPlacement
        ? new LocalTaskBoardReadView({placement: selectedTaskPlacement, task: selectedTask})
        : null,
      [selectedTask, selectedTaskPlacement]
    )
    const project = projectState.results[0] || null

    this.boardID = boardID
    this.breakpoint = useBreakpoint()
    this.colors = useThemeColors()
    this.currentQueryParams = currentQueryParams
    this.currentUserId = currentUserId
    this.params = params
    this.projectID = projectID
    this.routeActiveFilter = routeActiveFilter
    this.routeFocused = routeFocused
    this.routeSearchQuery = routeSearchQuery
    this.routeVisibleTaskStates = routeVisibleTaskStates
    this.router = useRouter()
    this.routeSelectedTaskID = routeSelectedTaskID
    this.themeName = useThemeName()
    this.windowDimensions = useWindowDimensions()
    this.online = useProjectSyncOnline()
    this.replica = replica
    this.projectName = project?.name() || ""
    this.project = project
    this.projectSyncUnavailable = projectSyncUnavailable
    this.queryFailed = Boolean(readError)
    this.visibleTaskIds = visibleTaskIds
    this.assignedUsersByTaskId = usersByTaskId(visibleAssignments, recordsById(usersState.results))

    useNow(() => {
      if (!queryLoading) {
        this.s.boardData = boardData
        this.s.boardRevision += 1
      }
      this.s.loading = loading
      this.s.notFound = !loading && !readError && !board
      this.s.currentUserId = currentUserId
      this.s.selectedTaskBoardDetails = selectedTaskBoard
      this.s.selectedTaskDetails = /** @type {Task | null} */ (/** @type {unknown} */ (selectedTask))
      this.s.status = readError ? {kind: "error", text: readError.message} : null
      this.s.taskTypeOptions = taskTypesState.results.map((taskType) => ({
        defaultForProject: taskType.defaultForProject(),
        id: taskType.id(),
        name: taskType.name() || ""
      }))
    }, [board, boardData, currentUserId, loading, queryLoading, readError, selectedTask, selectedTaskBoard, taskTypesState.results])

    useNow(() => {
      if (this.tt.routeActiveFilter !== this.lastRouteActiveFilter) {
        this.lastRouteActiveFilter = this.tt.routeActiveFilter
        this.s.activeFilter = this.tt.routeActiveFilter
      }
    }, [routeActiveFilter])
    useNow(() => {
      if (
        routeSearchContextKey !== this.lastRouteSearchContextKey ||
        this.tt.routeSearchQuery !== this.lastRouteSearchQuery
      ) {
        this.lastRouteSearchContextKey = routeSearchContextKey
        this.lastRouteSearchQuery = this.tt.routeSearchQuery
        this.s.searchQuery = this.tt.routeSearchQuery
      }
    }, [routeSearchContextKey, routeSearchQuery])
    useNow(() => {
      if (routeVisibleTaskStatesKey === this.lastRouteVisibleTaskStatesKey) return
      this.lastRouteVisibleTaskStatesKey = routeVisibleTaskStatesKey
      this.s.visibleTaskStates = this.tt.routeVisibleTaskStates
    }, [routeVisibleTaskStatesKey])
    useNow(() => {
      if (routeSelectedTaskID === this.lastRouteSelectedTaskId) return
      this.lastRouteSelectedTaskId = routeSelectedTaskID
      this.s.selectedTaskId = routeSelectedTaskID
    }, [routeSelectedTaskID])
    const selectedTaskPlacementContextKey = `${projectID}:${boardID}:${routeSelectedTaskID}`
    useNow(() => {
      if (selectedTaskPlacementContextKey !== this.lastSelectedTaskPlacementContextKey) {
        this.lastSelectedTaskPlacementContextKey = selectedTaskPlacementContextKey
        this.selectedTaskPlacementObserved = Boolean(selectedTaskBoard)
        return
      }
      if (queryLoading) return
      if (selectedTaskBoard) {
        this.selectedTaskPlacementObserved = true
      } else if (routeSelectedTaskID && this.selectedTaskPlacementObserved) {
        this.selectedTaskPlacementObserved = false
        this.onTaskDetailClosePress()
      }
    }, [queryLoading, selectedTaskBoard, selectedTaskPlacementContextKey])
  }

  /**
   * Renders the board screen and its modal stack.
   * @returns {ReactNode} Board screen content.
   */
  render() {
    const {activeFilter, boardData, loading, notFound, searchQuery, selectedTaskId, status, visibleTaskStates} = this.s
    const board = boardData?.board || null
    const projectSyncUnavailable = this.projectSyncUnavailable
    const sortedColumns = this.sortedBoardColumns()
    const selectedTaskBoard = selectedTaskId && this.s.selectedTaskBoardDetails?.taskId() === selectedTaskId
      ? this.s.selectedTaskBoardDetails
      : null
    const selectedTask = selectedTaskId && this.s.selectedTaskDetails?.id() === selectedTaskId
      ? this.s.selectedTaskDetails
      : null
    const selectedTaskMissing = Boolean(
      board && selectedTaskId && !selectedTaskBoard
    )
    const editingColumn = this.columnForId(this.s.editingColumnId)
    const editingTaskBoard = this.taskBoardForId(this.s.editingTaskBoardId)
    const editingTask = editingTaskBoard?.task() || null
    const remoteEditingColumn = editingColumn ? remoteColumnForCommand(editingColumn) : null
    const remoteEditingTask = editingTask ? remoteTaskForCommand(editingTask) : null
    const selectedTaskVisible = Boolean(this.tt.routeFocused && selectedTask && selectedTaskBoard && !editingTaskBoard && this.tt.routeSelectedTaskID)
    const selectedTaskCommentParams = selectedTask
      ? `resourceType=Task&resourceId=${selectedTask.id()}&projectId=${encodeURIComponent(this.tt.projectID)}`
      : ""
    const requestAutomationChangesHref = selectedTask
      ? buildRoutePath(
        this.tt.currentQueryParams(),
        `/comments/new?${selectedTaskCommentParams}&defaultComment=${encodeURIComponent("/changes ")}`
      )
      : undefined
    const cancelAutomationRunHref = selectedTask
      ? buildRoutePath(
        this.tt.currentQueryParams(),
        `/comments/new?${selectedTaskCommentParams}&defaultComment=${encodeURIComponent("/cancel ")}`
      )
      : undefined
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    let boardViewportWidth = this.tt.windowDimensions.width
    if (this.tt.breakpoint.mdUp) {
      boardViewportWidth -= BOARD_LAYOUT_SIDEBAR_WIDTH
    }
    boardViewportWidth = Math.max(0, boardViewportWidth)
    return (
      <View
        style={styles[`screen-${this.tt.routeFocused}`] ||= {
          minHeight: "100%",
          minWidth: 0,
          opacity: this.tt.routeFocused ? 1 : 0,
          position: this.tt.routeFocused ? "relative" : "absolute",
          visibility: this.tt.routeFocused ? "visible" : "hidden",
          width: "100%"
        }}
        testID="projectBoardScreen"
      >
        <ScreenTitle>{_("Board")}</ScreenTitle>
        <View
          style={styles[`header-${themeName}`] ||= {
            backgroundColor: colors.surface,
            borderBottomColor: colors.divider,
            borderBottomWidth: 1,
            paddingHorizontal: 24,
            paddingVertical: 20
          }}
          testID="projectBoardScreen/header"
        >
          <Text
            style={styles[`subtitle-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 6
            }}
            testID="projectBoardScreen/subtitle"
          >
            {this.projectName || _("Project")}
          </Text>
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.text,
              fontSize: 28,
              fontWeight: 700
            }}
            testID="projectBoardScreen/title"
          >
            {board?.name() || _("Board")}
          </Text>
        </View>
        {projectBoardControlsAvailable({loading, notFound, projectSyncUnavailable, queryFailed: this.queryFailed}) &&
          <ProjectBoardFilters
            activeFilter={activeFilter}
            onFilterPress={this.tt.onFilterPress}
            onNewColumnPress={this.tt.onNewColumnPress}
            onRefreshPress={this.tt.onRefreshBoardPress}
            onSearchChangeText={this.tt.onSearchChangeText}
            onTaskStatesButtonPress={this.tt.onTaskStatesButtonPress}
            onTaskStatesModalClosePress={this.tt.onTaskStatesModalClosePress}
            searchQuery={searchQuery}
            showTaskStatesModal={this.tt.routeFocused && this.s.showTaskStatesModal}
            taskStateOptions={taskStateOptions()}
            visibleTaskStates={visibleTaskStates}
            onTaskStateVisibilityChange={this.tt.onTaskStateVisibilityChange}
          />
        }
        {loading &&
          <View
            style={styles.loadingContainer ||= {
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              padding: 24
            }}
            testID="projectBoardScreen/loading"
          >
            <ActivityIndicator color={colors.link} size="small" />
            <Text
              style={styles[`loadingText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16,
                fontWeight: 600,
                marginTop: 12
              }}
              testID="projectBoardScreen/loadingText"
            >
              {_("Loading board...")}
            </Text>
          </View>
        }
        {projectSyncUnavailable &&
          <View
            style={styles.projectSyncUnavailable ||= {
              alignItems: "center",
              flex: 1,
              gap: 12,
              justifyContent: "center",
              padding: 24
            }}
            testID="projectBoardScreen/projectSyncUnavailable"
          >
            <Text
              style={styles[`projectSyncUnavailableText-${themeName}`] ||= {
                color: colors.dangerText,
                fontSize: 16,
                fontWeight: 600
              }}
              testID="projectBoardScreen/projectSyncUnavailableText"
            >
              {_("Failed to load board")}
            </Text>
            <ProjectSyncStatusIndicator />
          </View>
        }
        {!loading && notFound &&
          <View
            style={styles.stateContainer ||= {
              padding: 24
            }}
            testID="projectBoardScreen/notFound"
          >
            <Text
              style={styles[`stateText-${themeName}`] ||= {
                color: colors.text,
                fontSize: 16,
                fontWeight: 600
              }}
              testID="projectBoardScreen/notFoundText"
            >
              {_("Board not found.")}
            </Text>
          </View>
        }
        {!loading && !notFound && !this.queryFailed && !projectSyncUnavailable &&
          <ScrollView
            contentContainerStyle={styles.columnsContainer ||= {
              padding: 24
            }}
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles[`columnsScroll-${boardViewportWidth}`] ||= {
              flexGrow: 0,
              flexShrink: 0,
              maxWidth: boardViewportWidth,
              minWidth: 0,
              width: boardViewportWidth
            }}
            testID="projectBoardScreen/columnsScroll"
          >
            <View
              style={styles.columnsRow ||= {
                alignItems: "flex-start",
                flexDirection: "row"
              }}
              testID="projectBoardScreen/columnsRow"
            >
              {sortedColumns.map((column, columnIndex) => (
                <ProjectBoardColumnCard
                  assignedUsersByTaskId={this.assignedUsersByTaskId}
                  column={column}
                  dragPreview={this.s.dragPreview}
                  key={column.id()}
                  onAddTaskPress={this.tt.onAddTaskPress}
                  onBoardColumnDragEnd={this.tt.onBoardColumnDragEnd}
                  onBoardColumnDragMove={this.tt.onBoardColumnDragMove}
                  onBoardColumnDragStart={this.tt.onBoardColumnDragStart}
                  onBoardColumnMoveEnd={this.tt.onBoardColumnMoveEnd}
                  onColumnLayoutChange={this.tt.onBoardColumnLayoutChange}
                  onColumnMenuPress={this.tt.onColumnMenuPress}
                  onTaskBoardDragEnd={this.tt.onTaskBoardDragEnd}
                  onTaskBoardDragMove={this.tt.onTaskBoardDragMove}
                  onTaskBoardDragStart={this.tt.onTaskBoardDragStart}
                  onTaskBoardLayoutChange={this.tt.onTaskBoardLayoutChange}
                  onTaskBoardMenuPress={this.tt.onTaskBoardMenuPress}
                  onTaskBoardMoveEnd={this.tt.onTaskBoardMoveEnd}
                  onTaskBoardPress={this.tt.onTaskBoardPress}
                  orderIndex={columnIndex}
                  previewOffsetX={this.previewOffsetXForColumn(column)}
                  settleOffsetX={this.settleOffsetXForColumn(column)}
                  taskBoards={this.visibleTaskBoardsForColumn(column)}
                />
              ))}
            </View>
          </ScrollView>
        }
        {(status || selectedTaskMissing) &&
          <View
            style={styles.statusContainer ||= {
              paddingHorizontal: 24,
              paddingBottom: 24
            }}
            testID="projectBoardScreen/status"
          >
            <Text
              style={styles[`statusText-${themeName}-${status?.kind || "error"}`] ||= {
                color: status?.kind === "success" ? colors.successText : colors.dangerText,
                fontSize: 14,
                fontWeight: 600
              }}
              testID="projectBoardScreen/statusText"
            >
              {status?.text || _("Task not found on this board.")}
            </Text>
          </View>
        }
        {this.tt.routeFocused &&
          <>
            <TaskDetailModal
              approvingAutomationPlan={this.s.approvingAutomationPlan}
              cancelAutomationRunHref={cancelAutomationRunHref}
              currentBoardId={this.tt.boardID}
              onApproveAutomationPlanPress={this.tt.onTaskDetailApproveAutomationPlanPress}
              onClosePress={this.tt.onTaskDetailClosePress}
              onCurrentBoardPlacementRemoved={this.tt.onCurrentBoardPlacementRemoved}
              onDeletePress={this.tt.onTaskDetailDeletePress}
              onDismiss={this.tt.onTaskDetailDismiss}
              onEditPress={this.tt.onTaskDetailEditPress}
              onMarkAgentReadyPress={this.tt.onTaskDetailMarkAgentReadyPress}
              params={this.tt.currentQueryParams()}
              online={this.online}
              project={this.project}
              projectId={this.tt.projectID}
              replica={this.replica}
              requestAutomationChangesHref={requestAutomationChangesHref}
              task={selectedTask}
              testID="projectBoardTaskDetailModal"
              visible={selectedTaskVisible}
            />
            <ProjectBoardColumnMenu
              isVisible={Boolean(this.s.showColumnMenuForColumnId)}
              onAddTaskPress={this.tt.onColumnMenuAddTaskPress}
              onClosePress={this.tt.onColumnMenuClosePress}
              onDeletePress={this.tt.onColumnMenuDeletePress}
              onEditPress={this.tt.onColumnMenuEditPress}
            />
            <ProjectBoardTaskMenu
              isVisible={Boolean(this.s.showTaskMenuForTaskBoardId)}
              onClosePress={this.tt.onTaskMenuClosePress}
              onDeletePress={this.tt.onTaskMenuDeletePress}
              onEditPress={this.tt.onTaskMenuEditPress}
            />
            <ProjectBoardDeleteConfirmModal
              bodyText={this.deleteConfirmBodyText()}
              confirmLabel={_("Delete")}
              isVisible={Boolean(this.s.confirmingDelete)}
              onCancelPress={this.tt.onDeleteConfirmCancelPress}
              onConfirmPress={this.tt.onDeleteConfirmPress}
              saving={false}
              testID="projectBoardDeleteConfirmModal"
              title={_("Confirm delete")}
            />
            <ProjectBoardNewColumnModal
              boardId={this.tt.boardID}
              column={remoteEditingColumn}
              currentColumnCount={sortedColumns.length}
              isVisible={this.s.showNewColumnModal || Boolean(editingColumn)}
              onClosePress={this.tt.onNewColumnModalClosePress}
              onCreated={this.tt.onColumnCreated}
              onUpdated={this.tt.onColumnUpdated}
            />
            <ProjectBoardNewTaskModal
              boardColumnOptions={this.boardColumnOptions()}
              boardId={this.tt.boardID}
              boardProjectId={this.tt.projectID}
              columnId={this.s.showNewTaskModalForColumnId || undefined}
              currentUserId={this.s.currentUserId}
              isVisible={Boolean(this.s.showNewTaskModalForColumnId)}
              onClosePress={this.tt.onNewTaskModalClosePress}
              onCreated={this.tt.onTaskCreated}
              online={this.online}
              replica={this.replica}
              taskBoardsCount={this.taskBoardsCountForColumnId(
                this.s.showNewTaskModalForColumnId
              )}
              taskTypeOptions={this.s.taskTypeOptions}
            />
            <ProjectBoardNewTaskModal
              allowTaskTypeChange={Boolean(
                !editingTask ||
                editingTask.projectId() === this.tt.projectID
              )}
              boardColumnOptions={this.boardColumnOptions()}
              boardProjectId={this.tt.projectID}
              currentUserId={this.s.currentUserId}
              initialColumnId={editingTaskBoard?.boardColumnId() || undefined}
              isVisible={Boolean(editingTaskBoard && remoteEditingTask)}
              onClosePress={this.tt.onEditTaskModalClosePress}
              onUpdated={this.tt.onTaskUpdated}
              online={this.online}
              replica={this.replica}
              task={
                editingTaskBoard
                  ? remoteEditingTask
                  : null
              }
              taskBoardsCount={this.taskBoardsCountForColumnId(
                editingTaskBoard?.boardColumnId() || null
              )}
              taskTypeOptions={this.s.taskTypeOptions}
            />
          </>
        }
      </View>
    )
  }

  /**
   * Returns task boards visible in a board column after filters.
   * @param {BoardColumn} column Column whose task boards should be filtered.
   * @returns {TaskBoard[]} Filtered and sorted task-board records.
   */
  visibleTaskBoardsForColumn(column) {
    return localTaskBoardsForColumn(this.s.boardData, column.id())
      .filter((taskBoard) => this.visibleTaskIds.has(taskBoard.taskId()))
  }

  /**
   * Sorts loaded board columns by position.
   * @returns {BoardColumn[]} Board columns in display order.
   */
  sortedBoardColumns() {
    return localBoardColumns(this.s.boardData)
  }

  /**
   * Returns a column's one-based rendered position.
   * @param {BoardColumn} column Board column to locate.
   * @returns {number} One-based position in the current board.
   * @throws {Error} When the column is missing from the loaded board.
   */
  positionForColumn(column) {
    const columnIndex = this.sortedBoardColumns().findIndex(
      (candidate) => candidate.id() === column.id()
    )
    if (columnIndex === -1) {
      throw new Error(`Expected board column ${column.id()} to exist on board ${this.tt.boardID}`)
    }
    return columnIndex + 1
  }

  /**
   * Calculates live spacing offset for a board column.
   * @param {BoardColumn} column Board column whose preview offset should be read.
   * @returns {number} Horizontal preview offset.
   */
  previewOffsetXForColumn(column) {
    const columnDragPreview = this.s.columnDragPreview
    if (!columnDragPreview || column.id() === columnDragPreview.columnId) {
      return 0
    }
    const columns = this.sortedBoardColumns()
    const sourceIndex = columns.findIndex(
      (candidate) => candidate.id() === columnDragPreview.columnId
    )
    const columnIndex = columns.findIndex(
      (candidate) => candidate.id() === column.id()
    )
    if (sourceIndex === -1 || columnIndex === -1) {
      return 0
    }
    const targetColumns = columns.filter(
      (candidate) => candidate.id() !== columnDragPreview.columnId
    )
    const targetIndex = normalizedInsertionIndex(
      columnDragPreview.targetPosition,
      targetColumns.length
    )
    const draggedSlotWidth = this.draggedColumnSlotWidth(columnDragPreview)

    if (
      targetIndex > sourceIndex &&
      columnIndex > sourceIndex &&
      columnIndex <= targetIndex
    ) {
      return -draggedSlotWidth
    }
    if (
      targetIndex < sourceIndex &&
      columnIndex >= targetIndex &&
      columnIndex < sourceIndex
    ) {
      return draggedSlotWidth
    }
    return 0
  }

  /**
   * Calculates the dragged column's release-settle offset.
   * @param {BoardColumn} column Board column whose settle offset should be read.
   * @returns {number} Horizontal settle offset from original slot to target slot.
   */
  settleOffsetXForColumn(column) {
    const columnDragPreview = this.s.columnDragPreview
    const columns = this.sortedBoardColumns()
    if (!columnDragPreview || column.id() !== columnDragPreview.columnId) {
      return 0
    }
    const sourceIndex = columns.findIndex(
      (candidate) => candidate.id() === columnDragPreview.columnId
    )
    if (sourceIndex === -1) {
      return 0
    }
    const targetColumns = columns.filter(
      (candidate) => candidate.id() !== columnDragPreview.columnId
    )
    const targetIndex = normalizedInsertionIndex(
      columnDragPreview.targetPosition,
      targetColumns.length
    )
    return this.columnSlotLeftForColumns(targetColumns, targetIndex) -
      this.columnSlotLeftForColumns(columns, sourceIndex)
  }

  /**
   * Builds column select options for task modals.
   * @returns {{id: string, name: string}[]} Choices for column selects.
   */
  boardColumnOptions() {
    return this.sortedBoardColumns().map((boardColumn) => ({
      id: boardColumn.id(),
      name: boardColumn.name() || _("Untitled column")
    }))
  }

  /**
   * Counts task boards in a column.
   * @param {string | null} columnId Column identifier to count.
   * @returns {number} Number of loaded task boards in the column.
   * @throws {Error} When the requested column is missing from the loaded board.
   */
  taskBoardsCountForColumnId(columnId) {
    if (columnId === null) return 0
    const column = this.columnForId(columnId)
    if (!column) {
      throw new Error(
        `Expected board column ${columnId} to exist on board ${this.tt.boardID}`
      )
    }
    return localTaskBoardsForColumn(this.s.boardData, column.id()).length
  }

  /**
   * Finds a task board by its task id.
   * @param {number | string} taskID Task identifier to find.
   * @returns {TaskBoard | null} Matching task board, if loaded.
   */
  taskBoardForTaskId(taskID) {
    return localTaskBoardForTaskId(this.s.boardData, taskID)
  }

  /**
   * Finds a task board by id.
   * @param {string | null} taskBoardId Task-board identifier to find.
   * @returns {TaskBoard | null} Matching task board, if loaded.
   */
  taskBoardForId(taskBoardId) {
    if (taskBoardId === null) {
      return null
    }
    return this.s.boardData?.taskBoardsById[String(taskBoardId)] || null
  }

  /**
   * Finds a board column by id.
   * @param {string | null} columnId Column identifier to find.
   * @returns {BoardColumn | null} Matching column, if loaded.
   */
  columnForId(columnId) {
    if (columnId === null) {
      return null
    }
    return this.s.boardData?.columnsById[String(columnId)] || null
  }

  /**
   * Builds the delete confirmation body for the active delete target.
   * @returns {string} Confirmation message.
   */
  deleteConfirmBodyText() {
    const confirmingDelete = this.s.confirmingDelete
    if (!confirmingDelete) {
      return ""
    }
    if (confirmingDelete.type === "column") {
      const columnName =
        this.columnForId(confirmingDelete.columnId || null)?.name() ||
        _("Untitled column")
      return _("Delete the column %{name}?", {
        name: columnName
      })
    }
    return _("Delete this task?")
  }

  /**
   * Opens the task details modal for a task board.
   * @param {TaskBoard} taskBoard Task board whose task should be selected.
   * @returns {void} Nothing.
   * @throws {Error} When the task board is missing its task id.
   */
  onTaskBoardPress = (taskBoard) => {
    const taskID = taskBoard.taskId()
    if (taskID === null || taskID === undefined) {
      throw new Error(`Expected task board ${taskBoard.id()} to have a taskID`)
    }
    delete dismissedRouteSelectedTaskIds[`${this.tt.projectID}:${this.tt.boardID}`]
    this.s.selectedTaskId = String(taskID)
    const nextPath = pathForQueryParamUpdates(
      this.tt.currentQueryParams(),
      `/projects/${this.tt.projectID}/boards/${this.tt.boardID}`,
      {task_id: String(taskID)},
      {excludeKeys: ["boardId", "projectId"]}
    )
    if (nextPath) {
      this.tt.router.replace(nextPath)
    }
  }

  /**
   * Starts closing the task detail modal by clearing local selection so the
   * modal fades out. The route is updated later in onTaskDetailDismiss once the
   * fade finishes, because replacing the route remounts the board and would
   * otherwise drop the modal instantly without animating.
   * @returns {void} Nothing.
   */
  onTaskDetailClosePress = () => {
    if (this.s.selectedTaskId) {
      dismissedRouteSelectedTaskIds[`${this.tt.projectID}:${this.tt.boardID}`] = this.s.selectedTaskId
    }
    this.s.selectedTaskId = ""
  }

  /**
   * Removes the task_id route param after the detail modal finishes fading out.
   * Skips navigation when another task was selected during the fade.
   * @returns {void} Nothing.
   */
  onTaskDetailDismiss = () => {
    if (this.s.selectedTaskId) {
      return
    }
    const nextPath = pathForQueryParamUpdates(
      this.tt.currentQueryParams(),
      `/projects/${this.tt.projectID}/boards/${this.tt.boardID}`,
      {task_id: undefined},
      {excludeKeys: ["boardId", "projectId"]}
    )
    if (nextPath) {
      this.tt.router.replace(nextPath)
    }
  }

  /**
   * Closes the modal after sync removes its current Board placement.
   * @param {import("../../../../../shared/task-board-placement-command-types.js").TaskBoardRemoveFromBoardResponse} response Removal response.
   * @returns {void} Nothing.
   */
  onCurrentBoardPlacementRemoved = (response) => {
    void response
    this.onTaskDetailClosePress()
  }

  /**
   * Opens the new-column modal.
   * @returns {void} Nothing.
   */
  onNewColumnPress = () => {
    void this.openNewColumnModal()
  }

  /**
   * Opens the online-only new-column modal.
   * @returns {Promise<void>} Resolves after modal state is updated.
   */
  async openNewColumnModal() {
    if (!this.requireOnline()) return
    this.s.showNewColumnModal = true
  }

  /**
   * Retries the app-owned project sync coordinator.
   * @returns {Promise<void>} Resolves after retry handling finishes.
   * @rejects {unknown} When project sync retry fails.
   */
  onRefreshBoardPress = async () => {
    try {
      await projectSyncLifecycle.retry()
    } catch (error) {
      if (!this.isMounted() || !this.tt.routeFocused) return
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Closes the column modal and clears edited column state.
   * @returns {void} Nothing.
   */
  onNewColumnModalClosePress = () => {
    this.s.editingColumnId = null
    this.s.showNewColumnModal = false
  }

  /**
   * Opens the new-task modal for a column.
   * @param {BoardColumn} column Column that should receive the task.
   * @returns {void} Nothing.
   */
  onAddTaskPress = (column) => {
    void this.openNewTaskModal(column)
  }

  /**
   * Opens the column actions menu.
   * @param {BoardColumn} column Column whose menu should open.
   * @returns {void} Nothing.
   */
  onColumnMenuPress = (column) => {
    this.s.showColumnMenuForColumnId = column.id()
  }

  /**
   * Closes the column actions menu.
   * @returns {void} Nothing.
   */
  onColumnMenuClosePress = () => {
    this.s.showColumnMenuForColumnId = null
  }

  /**
   * Opens the new-task modal from the column menu.
   * @returns {Promise<void>} Resolves after modal opening is handled.
   */
  onColumnMenuAddTaskPress = async () => {
    const column = this.columnForId(this.s.showColumnMenuForColumnId)
    this.s.showColumnMenuForColumnId = null
    if (column) {
      await this.openNewTaskModal(column)
    }
  }

  /**
   * Opens the column modal for the selected menu column.
   * @returns {void} Nothing.
   */
  onColumnMenuEditPress = () => {
    if (!this.requireOnline()) return

    this.s.editingColumnId = this.s.showColumnMenuForColumnId
    this.s.showColumnMenuForColumnId = null
  }

  /**
   * Opens delete confirmation for the selected menu column.
   * @returns {void} Nothing.
   */
  onColumnMenuDeletePress = () => {
    const columnId = this.s.showColumnMenuForColumnId
    if (columnId === null) {
      return
    }
    this.s.confirmingDelete = {
      columnId,
      type: "column"
    }
    this.s.showColumnMenuForColumnId = null
  }

  /**
   * Opens the task actions menu.
   * @param {TaskBoard} taskBoard Task board whose menu should open.
   * @returns {void} Nothing.
   */
  onTaskBoardMenuPress = (taskBoard) => {
    this.s.showTaskMenuForTaskBoardId = taskBoard.id()
  }

  /**
   * Stores a board column layout in board-row coordinates.
   * @param {BoardColumn} column Measured column.
   * @param {BoardItemLayout} layout Position and size reported by React Native.
   * @returns {void} Nothing.
   */
  onBoardColumnLayoutChange = (column, layout) => {
    this.boardColumnLayouts.set(column.id(), layout)
  }

  /**
   * Starts live drag-preview spacing for a board column.
   * @param {BoardColumn} column Board column that started dragging.
   * @returns {void} Nothing.
   */
  onBoardColumnDragStart = (column) => {
    const sourcePosition = this.positionForColumn(column)
    const dragStartCenter = this.boardColumnCenterForDrag(column)

    if (dragStartCenter) {
      this.columnDragStartCenters.set(column.id(), dragStartCenter)
    }

    this.s.columnDragPreview = {
      columnId: column.id(),
      sourcePosition,
      targetPosition: sourcePosition
    }
  }

  /**
   * Updates live drag-preview spacing for a board column.
   * @param {BoardColumn} column Board column being dragged.
   * @param {object} event Native responder payload from PanResponder.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @returns {void} Nothing.
   */
  onBoardColumnDragMove = (column, event, gestureState) => {
    void event
    const targetPosition = this.columnPositionForDrag(gestureState, column)
    if (targetPosition === null) {
      return
    }
    const nextColumnDragPreview = {
      columnId: column.id(),
      sourcePosition: this.positionForColumn(column),
      targetPosition
    }
    if (!columnDragPreviewsMatch(this.s.columnDragPreview, nextColumnDragPreview)) {
      this.s.columnDragPreview = nextColumnDragPreview
    }
  }

  /**
   * Clears live drag-preview spacing for a board column.
   * @param {BoardColumn} column Board column that stopped dragging.
   * @returns {void} Nothing.
   */
  onBoardColumnDragEnd = (column) => {
    this.columnDragStartCenters.delete(column.id())
    if (this.s.columnDragPreview?.columnId === column.id()) {
      this.s.columnDragPreview = null
    }
  }

  /**
   * Handles the end of a board-column drag operation.
   * @param {BoardColumn} column Board column that was dragged.
   * @param {object} event Drag release event.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @returns {Promise<void>} Resolves after any move operation finishes.
   */
  onBoardColumnMoveEnd = async (column, event, gestureState) => {
    void event
    const targetPosition = this.columnPositionForDrag(gestureState, column)
    if (targetPosition === null) {
      this.onBoardColumnDragEnd(column)
      return
    }
    try {
      await this.moveBoardColumnToPosition(column, targetPosition)
    } finally {
      this.onBoardColumnDragEnd(column)
    }
  }

  /**
   * Stores a task-card layout in board-row coordinates.
   * @param {TaskBoard} taskBoard Measured task board.
   * @param {BoardItemLayout} layout Task-card layout.
   * @returns {void} Nothing.
   */
  onTaskBoardLayoutChange = (taskBoard, layout) => {
    this.taskBoardLayouts.set(taskBoard.id(), layout)
  }

  /**
   * Starts live drag-preview spacing for a task card.
   * @param {TaskBoard} taskBoard Task board that started dragging.
   * @returns {void} Nothing.
   */
  onTaskBoardDragStart = (taskBoard) => {
    const sourceColumnId = taskBoard.boardColumnId()
    const dragStartCenter = this.taskBoardCenterForDrag(taskBoard)

    if (dragStartCenter) {
      this.dragStartCenters.set(taskBoard.id(), dragStartCenter)
    }

    this.s.dragPreview = {
      rowNumber: this.visibleRowNumberForTaskBoard(taskBoard),
      sourceColumnId,
      targetColumnId: sourceColumnId,
      taskBoardId: taskBoard.id()
    }
  }

  /**
   * Updates live drag-preview spacing for a task card.
   * @param {TaskBoard} taskBoard Task board being dragged.
   * @param {object} event Native responder payload from PanResponder.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @returns {void} Nothing.
   */
  onTaskBoardDragMove = (taskBoard, event, gestureState) => {
    void event
    const dropTarget = this.dropTargetForDrag(gestureState, taskBoard)
    if (!dropTarget) {
      return
    }
    const nextDragPreview = {
      rowNumber: dropTarget.previewRowNumber,
      sourceColumnId: taskBoard.boardColumnId(),
      targetColumnId: dropTarget.column.id(),
      taskBoardId: taskBoard.id()
    }
    if (!dragPreviewsMatch(this.s.dragPreview, nextDragPreview)) {
      this.s.dragPreview = nextDragPreview
    }
  }

  /**
   * Clears live drag-preview spacing for a task card.
   * @param {TaskBoard} taskBoard Task board that stopped dragging.
   * @returns {void} Nothing.
   */
  onTaskBoardDragEnd = (taskBoard) => {
    this.dragStartCenters.delete(taskBoard.id())
    if (this.s.dragPreview?.taskBoardId === taskBoard.id()) {
      this.s.dragPreview = null
    }
  }

  /**
   * Handles the end of a task-card drag operation.
   * @param {TaskBoard} taskBoard Task board that was dragged.
   * @param {object} event Drag release event.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @returns {Promise<void>} Resolves after any move operation finishes.
   */
  onTaskBoardMoveEnd = async (taskBoard, event, gestureState) => {
    void event
    const dropTarget = this.dropTargetForDrag(gestureState, taskBoard)
    if (!dropTarget) {
      this.onTaskBoardDragEnd(taskBoard)
      return
    }
    try {
      await this.moveTaskBoardToTarget(
        taskBoard,
        dropTarget.column,
        dropTarget.rowNumber
      )
    } finally {
      this.onTaskBoardDragEnd(taskBoard)
    }
  }

  /**
   * Closes the task actions menu.
   * @returns {void} Nothing.
   */
  onTaskMenuClosePress = () => {
    this.s.showTaskMenuForTaskBoardId = null
  }

  /**
   * Opens the task modal for the selected menu task.
   * @returns {void} Nothing.
   */
  onTaskMenuEditPress = () => {
    if (!this.requireOnline()) return
    this.s.editingTaskBoardId = this.s.showTaskMenuForTaskBoardId
    this.s.showTaskMenuForTaskBoardId = null
  }

  /**
   * Clears selection used only for loading a task-menu edit modal.
   * @returns {void} Nothing.
   */
  clearTaskMenuEditSelection() {
    const editingTaskBoard = this.taskBoardForId(this.s.editingTaskBoardId)
    if (
      editingTaskBoard &&
      String(editingTaskBoard.taskId()) === this.s.selectedTaskId &&
      !this.tt.currentQueryParams().task_id
    ) {
      this.s.selectedTaskId = ""
      this.s.selectedTaskDetails = null
    }
  }

  /**
   * Opens delete confirmation for the selected menu task.
   * @returns {void} Nothing.
   */
  onTaskMenuDeletePress = () => {
    const taskBoardId = this.s.showTaskMenuForTaskBoardId
    if (taskBoardId === null) {
      return
    }
    this.s.confirmingDelete = {
      taskBoardId,
      type: "task"
    }
    this.s.showTaskMenuForTaskBoardId = null
  }

  /**
   * Opens the task modal from task details.
   * @returns {void} Nothing.
   */
  onTaskDetailEditPress = () => {
    if (!this.requireOnline()) return
    const taskBoard = this.taskBoardForTaskId(this.s.selectedTaskId)
    if (!taskBoard) {
      return
    }
    this.s.editingTaskBoardId = taskBoard.id()
  }

  /**
   * Opens delete confirmation from task details.
   * @returns {void} Nothing.
   */
  onTaskDetailDeletePress = () => {
    const taskBoard = this.taskBoardForTaskId(this.s.selectedTaskId)
    if (!taskBoard) {
      return
    }
    this.s.confirmingDelete = {
      taskBoardId: taskBoard.id(),
      type: "task"
    }
  }

  /**
   * Assigns the task shown in the detail modal to the automation runtime.
   * The existing SyncClient converges the resulting presentation fields.
   * @returns {Promise<void>} Resolves after the assignment request finishes.
   * @rejects {unknown} When assigning the task fails.
   */
  onTaskDetailMarkAgentReadyPress = async () => {
    if (!this.requireOnline()) return
    const task = this.s.selectedTaskDetails
    if (!task) return
    try {
      await remoteTaskForCommand(task).assignToAgent()
      this.s.status = {kind: "success", text: _("The task was assigned to AI")}
    } catch (error) {
      if (!this.isMounted() || !this.tt.routeFocused) return
      this.s.status = {kind: "error", text: errorMessage(error)}
      throw error
    }
  }

  /**
   * Approves the selected task's automation plan by posting the owner-decision
   * comment that TensorBuzz polls for.
   * @returns {Promise<void>} Resolves after comment creation.
   * @rejects {unknown} When comment creation fails.
   */
  onTaskDetailApproveAutomationPlanPress = async () => {
    if (!this.requireOnline()) return
    const task = this.s.selectedTaskDetails
    if (!task) return
    this.s.approvingAutomationPlan = true
    try {
      await RemoteComment.create({
        comment: "/approve",
        resourceId: task.id(),
        resourceType: "Task"
      })
      this.s.status = {kind: "success", text: _("The automation plan was approved")}
    } catch (error) {
      if (!this.isMounted() || !this.tt.routeFocused) return
      this.s.status = {kind: "error", text: errorMessage(error)}
      throw error
    } finally {
      if (this.isMounted()) this.s.approvingAutomationPlan = false
    }
  }

  /**
   * Cancels the delete confirmation modal.
   * @returns {void} Nothing.
   */
  onDeleteConfirmCancelPress = () => {
    this.s.confirmingDelete = null
  }

  /**
   * Closes the new-task modal.
   * @returns {void} Nothing.
   */
  onNewTaskModalClosePress = () => {
    this.s.showNewTaskModalForColumnId = null
  }

  /**
   * Handles successful column creation.
   * @param {BoardColumn} column Model returned after saving the modal form.
   * @returns {void} Nothing.
   */
  onColumnCreated = (column) => {
    void column
    this.s.showNewColumnModal = false
  }

  /**
   * Handles successful column updates.
   * @param {BoardColumn} column Model returned after updating the modal form.
   * @returns {void} Nothing.
   */
  onColumnUpdated = (column) => {
    void column
    this.s.editingColumnId = null
    this.s.showNewColumnModal = false
  }

  /**
   * Handles successful task creation.
   * @param {TaskBoard} taskBoard Task-board relation returned by the create flow.
   * @returns {Promise<void>} Resolves after closing the modal; sync owns convergence.
   */
  onTaskCreated = async (taskBoard) => {
    void taskBoard
    this.s.showNewTaskModalForColumnId = null
  }

  /**
   * Handles successful task updates and optional column moves.
   * @param {string} boardColumnId Column that should contain the updated task.
   * @param {boolean} taskChanged Whether the task attributes changed.
   * @returns {Promise<void>} Resolves after the local board state updates.
   * @rejects {Error} When the selected destination column is missing.
   */
  onTaskUpdated = async (boardColumnId, taskChanged) => {
    const taskBoard = this.taskBoardForId(this.s.editingTaskBoardId)
    this.s.editingTaskBoardId = null
    if (!taskBoard) {
      return
    }
    if (taskBoard.boardColumnId() !== boardColumnId) {
      const targetColumn = this.columnForId(boardColumnId)
      if (!targetColumn) {
        throw new Error(`Expected board column ${boardColumnId} to exist`)
      }
      await this.moveTaskBoardToTarget(
        taskBoard,
        targetColumn,
        this.taskBoardsCountForColumnId(boardColumnId) + 1
      )
      return
    }
    void taskChanged
  }

  /**
   * Closes the edit-task modal.
   * @returns {void} Nothing.
   */
  onEditTaskModalClosePress = () => {
    this.clearTaskMenuEditSelection()
    this.s.editingTaskBoardId = null
  }

  /**
   * Applies or toggles a board filter.
   * @param {BoardFilterKey} filterKey Filter selected by the user.
   * @returns {Promise<void>} Resolves after the route is updated.
   */
  onFilterPress = async (filterKey) => {
    const nextFilter =
      filterKey === this.s.activeFilter && filterKey !== "all"
        ? "all"
        : filterKey
    if (nextFilter === "mine" && !this.s.currentUserId) {
      try {
        const currentUserId = await this.currentUserIdForCreate()
        if (!this.isMounted() || !this.tt.routeFocused) return
        this.s.currentUserId = currentUserId
      } catch (_error) {
        return
      }
    }
    this.s.activeFilter = nextFilter
    const nextPath = pathForQueryParamUpdates(
      this.tt.currentQueryParams(),
      `/projects/${this.tt.projectID}/boards/${this.tt.boardID}`,
      {
        filter: nextFilter === "all" ? undefined : nextFilter
      },
      {
        excludeKeys: ["boardId", "projectId"]
      }
    )
    if (nextPath) {
      this.tt.router.push(nextPath)
    }
  }

  /**
   * Updates the board states visible in task columns.
   * @param {string[]} visibleTaskStates State values that should remain visible.
   * @returns {void} Nothing.
   */
  onTaskStateVisibilityChange = (visibleTaskStates) => {
    this.s.visibleTaskStates = visibleTaskStates
    this.s.showTaskStatesModal = true
    const nextPath = pathForQueryParamUpdates(
      this.tt.currentQueryParams(),
      `/projects/${this.tt.projectID}/boards/${this.tt.boardID}`,
      {
        states: statesParamForVisibleTaskStates(visibleTaskStates)
      },
      {
        excludeKeys: ["boardId", "projectId"]
      }
    )
    if (nextPath) {
      this.tt.router.push(nextPath)
    }
  }

  /**
   * Updates the search input and schedules route navigation.
   * @param {string} value Search text entered by the user.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    this.s.searchQuery = value
  }

  /** Opens the task-state visibility modal. */
  onTaskStatesButtonPress = () => {
    this.s.showTaskStatesModal = true
  }

  /** Closes the task-state visibility modal. */
  onTaskStatesModalClosePress = () => {
    this.s.showTaskStatesModal = false
  }

  /**
   * Opens the new-task modal after loading current-user context.
   * @param {BoardColumn} column Column that should receive the task.
   * @returns {Promise<void>} Resolves after modal state is updated.
   */
  async openNewTaskModal(column) {
    try {
      // Task creation is allowed offline: the board is read from the local
      // replica, so only the loaded-board guard applies (not connectivity).
      if (!this.s.boardData) return
      const currentUserId = await this.currentUserIdForCreate()
      if (!this.isMounted() || !this.tt.routeFocused) return
      this.s.currentUserId = currentUserId
      this.s.showNewTaskModalForColumnId = column.id()
      this.s.status = null
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Loads the current user id for task creation.
   * @returns {Promise<string>} Current user identifier.
   * @throws {Error} When the current profile does not include a user id.
   * @rejects {Error} When the current profile cannot be loaded.
   */
  async currentUserIdForCreate() {
    const currentUserId = this.s.currentUserId || this.tt.currentUserId
    if (!currentUserId) {
      throw new Error("Current user could not be loaded")
    }
    return currentUserId
  }

  /**
   * Keeps unsupported mutations visibly online-only.
   * @returns {boolean} Whether a command may be sent.
   */
  requireOnline() {
    if (this.online) return true
    this.s.status = {
      kind: "error",
      text: _("This action requires an internet connection.")
    }
    return false
  }

  /**
   * Confirms deletion for the selected column or task.
   * @returns {Promise<void>} Resolves after delete handling finishes.
   */
  onDeleteConfirmPress = async () => {
    if (!this.requireOnline()) return
    const confirmingDelete = this.s.confirmingDelete
    if (!confirmingDelete) {
      return
    }
    this.s.confirmingDelete = null
    this.s.status = null
    try {
      if (confirmingDelete.type === "column") {
        const column = this.columnForId(confirmingDelete.columnId || null)
        if (!column) {
          throw new Error("Board column not found")
        }
        await remoteColumnForCommand(column).destroy()
      } else {
        const taskBoard = this.taskBoardForId(
          confirmingDelete.taskBoardId || null
        )
        const task = taskBoard ? taskBoard.task() : null
        if (!task) {
          throw new Error("Task not found")
        }
        await remoteTaskForCommand(task).destroy()
        this.onTaskDetailClosePress()
      }
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Sends an online column reorder and leaves local convergence to SyncClient.
   * @param {BoardColumn} column Record being moved.
   * @param {number} targetPosition One-based position in the board row.
   * @returns {Promise<void>} Resolves after the reorder command is accepted.
   * @rejects {Error} When board data is not loaded before moving.
   */
  async moveBoardColumnToPosition(column, targetPosition) {
    if (!this.requireOnline()) return
    if (!this.s.boardData) {
      throw new Error("Expected board data to exist before moving a column")
    }
    const columns = this.sortedBoardColumns()
    const sourceIndex = columns.findIndex(
      (candidate) => candidate.id() === column.id()
    )
    if (sourceIndex === -1) {
      throw new Error(`Expected board column ${column.id()} to exist before moving`)
    }
    const columnsWithoutMoved = columns.filter(
      (candidate) => candidate.id() !== column.id()
    )
    const targetIndex = normalizedInsertionIndex(
      targetPosition,
      columnsWithoutMoved.length
    )
    const nextColumns = columnsWithoutMoved.slice()
    nextColumns.splice(targetIndex, 0, column)
    const targets = nextColumns
      .map((boardColumn, index) => ({id: boardColumn.id(), position: index + 1}))
      .filter((target, index) => columns[index]?.id() !== target.id)
    if (targets.length === 0) return
    try {
      const reorderedColumns = await reorderBoardColumns(targets)
      const reorderedIds = new Set(reorderedColumns.map((boardColumn) => boardColumn.id()))
      for (const target of targets) {
        if (!reorderedIds.has(target.id)) {
          throw new Error(`Expected board-column reorder response for ${target.id}.`)
        }
      }
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Sends an online task-card reorder and leaves local convergence to SyncClient.
   * @param {TaskBoard} taskBoard Record being moved.
   * @param {BoardColumn} targetColumn Destination column.
   * @param {number} targetRowNumber One-based row number in the destination column.
   * @returns {Promise<void>} Resolves after the reorder command is accepted.
   * @rejects {Error} When board data is not loaded before moving.
   */
  async moveTaskBoardToTarget(taskBoard, targetColumn, targetRowNumber) {
    if (!this.requireOnline()) return
    if (!this.s.boardData) {
      throw new Error("Expected board data to exist before moving a task")
    }
    const targetTaskBoards = this.sortedTaskBoardsForColumn(targetColumn)
    const targetTaskBoardsWithoutMoved = targetTaskBoards.filter(
      (targetTaskBoard) => targetTaskBoard.id() !== taskBoard.id()
    )
    const targetInsertionIndex = normalizedInsertionIndex(
      targetRowNumber,
      targetTaskBoardsWithoutMoved.length
    )
    const rowNumber = targetInsertionIndex + 1
    if (
      taskBoard.boardColumnId() === targetColumn.id() &&
      taskBoard.rowNumber() === rowNumber
    ) {
      return
    }
    try {
      const reorderedTaskBoard = await reorderTaskBoards({
        boardColumnId: targetColumn.id(),
        id: taskBoard.id(),
        rowNumber,
        taskId: taskBoard.taskId()
      })
      if (reorderedTaskBoard.id() !== taskBoard.id()) {
        throw new Error(`Expected task-board reorder response for ${taskBoard.id()}.`)
      }
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Sorts loaded task boards for a column by row number.
   * @param {BoardColumn} column Column whose task boards should be sorted.
   * @returns {TaskBoard[]} Task boards ordered by saved row number.
   */
  sortedTaskBoardsForColumn(column) {
    return localTaskBoardsForColumn(this.s.boardData, column.id())
  }

  /**
   * Resolves the one-based board-column position under a drag gesture.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @param {BoardColumn} column Board column that was dragged.
   * @returns {number | null} Destination position, if one is available.
   */
  columnPositionForDrag(gestureState, column) {
    const position = this.columnInsertionPositionForGesture(gestureState, column)
    if (!position) {
      return null
    }
    const insertionIndex = boardColumnInsertionIndexAtPosition(
      position,
      this.sortedBoardColumns(),
      column.id(),
      this.boardColumnLayouts
    )
    return insertionIndex + 1
  }

  /**
   * Builds the board-row position used for column insertion thresholds.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @param {BoardColumn} column Dragged board column.
   * @returns {BoardDragPosition | null} Direction-aware insertion position.
   */
  columnInsertionPositionForGesture(gestureState, column) {
    const dragPosition = this.columnDragPositionForGesture(gestureState, column)
    if (!dragPosition || typeof gestureState.dx !== "number") {
      return null
    }
    if (gestureState.dx === 0) {
      return dragPosition
    }

    const layout = this.boardColumnLayouts.get(column.id())
    const columnWidth = layout?.width || PROJECT_BOARD_COLUMN_CARD_WIDTH

    return {
      x: dragPosition.x + (gestureState.dx < 0 ? -columnWidth / 2 : columnWidth / 2),
      y: dragPosition.y
    }
  }

  /**
   * Builds the board-row position used for column drag thresholds.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @param {BoardColumn} column Dragged board column.
   * @returns {BoardDragPosition | null} Dragged-column center position.
   */
  columnDragPositionForGesture(gestureState, column) {
    const dragStartCenter =
      this.columnDragStartCenters.get(column.id()) ||
      this.boardColumnCenterForDrag(column)
    if (
      !dragStartCenter ||
      typeof gestureState.dx !== "number" ||
      typeof gestureState.dy !== "number"
    ) {
      return null
    }
    return {
      x: dragStartCenter.x + gestureState.dx,
      y: dragStartCenter.y + gestureState.dy
    }
  }

  /**
   * Reads a board-column center in board-row coordinates.
   * @param {BoardColumn} column Board column whose rendered card should be measured.
   * @returns {BoardDragPosition | null} Board-column center, if measured.
   */
  boardColumnCenterForDrag(column) {
    const layout = this.boardColumnLayouts.get(column.id())
    if (!layout) {
      return null
    }
    return {
      x: layout.x + layout.width / 2,
      y: layout.y + layout.height / 2
    }
  }

  /**
   * Reads the dragged column slot width.
   * @param {BoardColumnDragPreview} columnDragPreview Current drag preview.
   * @returns {number} Slot width used for sibling offsets.
   */
  draggedColumnSlotWidth(columnDragPreview) {
    const column = this.columnForId(columnDragPreview.columnId)
    return column
      ? this.columnSlotWidth(column)
      : PROJECT_BOARD_COLUMN_CARD_WIDTH + PROJECT_BOARD_COLUMN_CARD_MARGIN_RIGHT
  }

  /**
   * Reads a board-column slot width.
   * @param {BoardColumn} column Board column whose slot width should be read.
   * @returns {number} Measured width plus horizontal gap.
   */
  columnSlotWidth(column) {
    const layout = this.boardColumnLayouts.get(column.id())
    return (layout?.width || PROJECT_BOARD_COLUMN_CARD_WIDTH) +
      PROJECT_BOARD_COLUMN_CARD_MARGIN_RIGHT
  }

  /**
   * Sums slot widths before an index.
   * @param {BoardColumn[]} columns Ordered board columns.
   * @param {number} endIndex Exclusive end index.
   * @returns {number} Left offset for the slot.
   */
  columnSlotLeftForColumns(columns, endIndex) {
    return columns
      .slice(0, endIndex)
      .reduce(
        (leftOffset, column) => leftOffset + this.columnSlotWidth(column),
        0
      )
  }

  /**
   * Resolves the board column and insertion row under a completed drag gesture.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @param {TaskBoard} taskBoard Task board that was dragged.
   * @returns {TaskBoardDropTarget | null} Destination, if one is available.
   */
  dropTargetForDrag(gestureState, taskBoard) {
    const position = this.dragPositionForGesture(gestureState, taskBoard)
    if (!position) {
      return null
    }
    const column = this.columnAtPosition(position)
    if (!column) {
      return null
    }
    const visibleInsertionIndex = this.visibleInsertionIndexForDropPosition(
      position,
      column,
      taskBoard
    )

    return {
      column,
      previewRowNumber: visibleInsertionIndex + 1,
      rowNumber: this.rowNumberForVisibleInsertionIndex(
        column,
        taskBoard,
        visibleInsertionIndex
      )
    }
  }

  /**
   * Builds the board-row position used for drag thresholds.
   * @param {{dx?: number, dy?: number}} gestureState Gesture state from the drag handler.
   * @param {TaskBoard} taskBoard Dragged task board.
   * @returns {BoardDragPosition | null} Dragged-card center position.
   */
  dragPositionForGesture(gestureState, taskBoard) {
    const dragStartCenter =
      this.dragStartCenters.get(taskBoard.id()) ||
      this.taskBoardCenterForDrag(taskBoard)
    if (
      !dragStartCenter ||
      typeof gestureState.dx !== "number" ||
      typeof gestureState.dy !== "number"
    ) {
      return null
    }
    return {
      x: dragStartCenter.x + gestureState.dx,
      y: dragStartCenter.y + gestureState.dy
    }
  }

  /**
   * Reads a task-card center in board-row coordinates.
   * @param {TaskBoard} taskBoard Task board whose rendered card should be measured.
   * @returns {BoardDragPosition | null} Task-card center, if measured.
   */
  taskBoardCenterForDrag(taskBoard) {
    const layout = this.taskBoardLayouts.get(taskBoard.id())
    if (!layout) {
      return null
    }
    return {
      x: layout.x + layout.width / 2,
      y: layout.y + layout.height / 2
    }
  }

  /**
   * Resolves a zero-based visible insertion index for a drag drop position.
   * @param {BoardDragPosition} position Dragged card center in board-row coordinates.
   * @param {BoardColumn} column Column receiving the task board.
   * @param {TaskBoard} taskBoard Dragged task board.
   * @returns {number} Zero-based visible insertion index.
   */
  visibleInsertionIndexForDropPosition(position, column, taskBoard) {
    return taskBoardInsertionIndexAtPosition(
      position,
      this.visibleTaskBoardsForColumn(column),
      taskBoard.id(),
      this.taskBoardLayouts
    )
  }

  /**
   * Translates a visible insertion slot into the backing full-column row.
   * @param {BoardColumn} column Column receiving the task board.
   * @param {TaskBoard} taskBoard Dragged task board.
   * @param {number} visibleInsertionIndex Zero-based insertion index among visible cards.
   * @returns {number} One-based full destination row number.
   * @throws {Error} When the dragged task board is missing required visible/full row state.
   */
  rowNumberForVisibleInsertionIndex(column, taskBoard, visibleInsertionIndex) {
    const visibleTaskBoards = this.visibleTaskBoardsForColumn(column)
    if (column.id() === taskBoard.boardColumnId()) {
      const currentVisibleIndex = visibleTaskBoards.findIndex(
        (candidate) => candidate.id() === taskBoard.id()
      )
      if (currentVisibleIndex === -1) {
        throw new Error(`Expected task board ${taskBoard.id()} to be visible before reordering`)
      }
      if (currentVisibleIndex === visibleInsertionIndex) {
        const currentRowNumber = taskBoard.rowNumber()
        if (!currentRowNumber) {
          throw new Error(`Expected task board ${taskBoard.id()} to have a row number before reordering`)
        }
        return currentRowNumber
      }
    }
    return fullRowNumberForVisibleInsertion({
      fullTaskBoards: this.sortedTaskBoardsForColumn(column),
      movedTaskBoard: taskBoard,
      visibleInsertionIndex,
      visibleTaskBoards
    })
  }

  /**
   * Resolves the visible row number for a rendered task card.
   * @param {TaskBoard} taskBoard Task board being dragged.
   * @returns {number} One-based visible row number.
   * @throws {Error} When the dragged task board is not visible.
   */
  visibleRowNumberForTaskBoard(taskBoard) {
    const column = this.columnForId(taskBoard.boardColumnId())
    if (!column) {
      throw new Error(`Expected column ${taskBoard.boardColumnId()} for task board ${taskBoard.id()}`)
    }
    const visibleIndex = this.visibleTaskBoardsForColumn(column).findIndex(
      (candidate) => candidate.id() === taskBoard.id()
    )
    if (visibleIndex === -1) {
      throw new Error(`Expected task board ${taskBoard.id()} to be visible before dragging`)
    }
    return visibleIndex + 1
  }

  /**
   * Finds the column at a board-row position.
   * @param {BoardDragPosition} position Board-row position.
   * @returns {BoardColumn | null} Destination column, if one can be resolved.
   */
  columnAtPosition(position) {
    const candidates = this.sortedBoardColumns()
      .map((column) => ({
        column,
        layout: this.boardColumnLayouts.get(column.id()) || null
      }))
      .filter(
        (candidate) => candidate.layout !== null
      )
    const directCandidate = candidates.find((candidate) => (
      candidate.layout &&
      position.x >= candidate.layout.x &&
      position.x <= candidate.layout.x + candidate.layout.width &&
      position.y >= candidate.layout.y &&
      position.y <= candidate.layout.y + candidate.layout.height
    ))
    if (directCandidate) {
      return directCandidate.column
    }
    return closestColumnForPosition(position, candidates)
  }
}

/** @type {NamedExoticComponent<ProjectBoardRouteProps>} */
const ProjectBoardRouteComponent = memo(shapeComponent(ProjectBoardRoute))

export default ProjectBoardRouteComponent

/**
 * Indexes records by durable id for relationship joins.
 * @template {{id: () => string}} RecordType - Record exposing a durable string id.
 * @param {RecordType[]} records Records to index.
 * @returns {Record<string, RecordType>} Records keyed by id.
 */
function recordsById(records) {
  return Object.fromEntries(records.map((record) => [record.id(), record]))
}

/**
 * Joins tenant-local assignments to catalog users for task cards.
 * @param {InstanceType<typeof LocalTaskAssignedUser>[]} assignments Local assignment rows.
 * @param {Record<string, InstanceType<typeof LocalUser>>} usersById Catalog users keyed by id.
 * @returns {Record<string, InstanceType<typeof LocalUser>[]>} Users keyed by task id.
 */
function usersByTaskId(assignments, usersById) {
  /** @type {Record<string, InstanceType<typeof LocalUser>[]>} */
  const result = {}
  for (const assignment of assignments) {
    const user = usersById[String(assignment.userId())]
    if (!user) continue
    ;(result[String(assignment.taskId())] ||= []).push(user)
  }
  return result
}

/**
 * Builds an online command instance from an immutable local column row.
 * @param {InstanceType<typeof LocalBoardColumn>} column Local column row.
 * @returns {RemoteBoardColumn} Remote command instance.
 */
function remoteColumnForCommand(column) {
  return RemoteBoardColumn.instantiateFromResponse({
    boardId: column.boardId(),
    color: column.color(),
    id: column.id(),
    name: column.name(),
    position: column.position()
  })
}

/**
 * Builds an online command instance from an immutable local task row.
 * @param {InstanceType<typeof LocalTask>} task Local task row.
 * @returns {RemoteTask} Remote command instance.
 */
function remoteTaskForCommand(task) {
  return RemoteTask.instantiateFromResponse({
    description: task.description(),
    id: task.id(),
    name: task.name(),
    priority: task.priority(),
    projectId: task.projectId(),
    state: task.state(),
    taskTypeId: task.taskTypeId(),
    userId: task.userId()
  })
}

/**
 * Normalizes a route filter value.
 * @param {string | null} value Route value to normalize.
 * @returns {BoardFilterKey} Supported board filter key.
 */
function normalizedFilterValue(value) {
  if (value === "mine" || value === "due_soon") {
    return value
  }
  return "all"
}

/**
 * Parses visible task states from the route's state list.
 * @param {string | null} statesParam Comma-separated visible task states.
 * @returns {string[]} Task states visible on the board.
 */
function visibleTaskStatesFromStatesParam(statesParam) {
  if (statesParam === null) {
    return DEFAULT_VISIBLE_TASK_STATES
  }
  const visibleStates = new Set()
  for (const state of statesParam.split(",")) {
    if (ALL_TASK_STATE_SET.has(state)) {
      visibleStates.add(state)
    }
  }
  return ALL_TASK_STATES.filter((state) => visibleStates.has(state))
}

/**
 * Builds the state route param for the current visible states.
 * @param {string[]} visibleTaskStates Task states visible on the board.
 * @returns {string | undefined} Comma-separated visible states or undefined for defaults.
 */
function statesParamForVisibleTaskStates(visibleTaskStates) {
  const visibleStates = new Set(visibleTaskStates)
  const orderedVisibleStates = ALL_TASK_STATES.filter((state) => visibleStates.has(state))
  const orderedVisibleStatesKey = orderedVisibleStates.join(",")

  return orderedVisibleStatesKey === DEFAULT_VISIBLE_TASK_STATES_KEY
    ? undefined
    : orderedVisibleStatesKey || "none"
}

/**
 * Bounds a one-based row number to an insertion index.
 * @param {number} rowNumber One-based row number.
 * @param {number} maxIndex Maximum allowed insertion index.
 * @returns {number} Zero-based insertion index.
 */
function normalizedInsertionIndex(rowNumber, maxIndex) {
  return Math.max(0, Math.min(rowNumber - 1, maxIndex))
}

/**
 * Checks whether two drag-preview targets are identical.
 * @param {TaskBoardDragPreview | null} currentDragPreview Preview target stored in route state.
 * @param {TaskBoardDragPreview} nextDragPreview Candidate drag preview.
 * @returns {boolean} Whether the preview target is unchanged.
 */
function dragPreviewsMatch(currentDragPreview, nextDragPreview) {
  return Boolean(
    currentDragPreview &&
    currentDragPreview.rowNumber === nextDragPreview.rowNumber &&
    currentDragPreview.sourceColumnId === nextDragPreview.sourceColumnId &&
    currentDragPreview.targetColumnId === nextDragPreview.targetColumnId &&
    currentDragPreview.taskBoardId === nextDragPreview.taskBoardId
  )
}

/**
 * Checks whether two column drag-preview targets are identical.
 * @param {BoardColumnDragPreview | null} currentColumnDragPreview Preview target stored in route state.
 * @param {BoardColumnDragPreview} nextColumnDragPreview Candidate drag preview.
 * @returns {boolean} Whether the preview target is unchanged.
 */
function columnDragPreviewsMatch(currentColumnDragPreview, nextColumnDragPreview) {
  return Boolean(
    currentColumnDragPreview &&
    currentColumnDragPreview.columnId === nextColumnDragPreview.columnId &&
    currentColumnDragPreview.sourcePosition === nextColumnDragPreview.sourcePosition &&
    currentColumnDragPreview.targetPosition === nextColumnDragPreview.targetPosition
  )
}

/**
 * Chooses the insertion index for a board column at a board-row position.
 * @param {BoardDragPosition} position Board-row position.
 * @param {BoardColumn[]} columns Ordered board columns.
 * @param {string} draggedColumnId Dragged board-column id to exclude.
 * @param {Map<string, BoardItemLayout>} boardColumnLayouts Measured board-column layouts.
 * @returns {number} Zero-based insertion index.
 */
function boardColumnInsertionIndexAtPosition(
    position,
    columns,
    draggedColumnId,
    boardColumnLayouts
  ) {
    const targetColumns = columns.filter(
      (column) => column.id() !== draggedColumnId
    )
  for (let index = 0; index < targetColumns.length; index += 1) {
    const layout = boardColumnLayouts.get(targetColumns[index].id())
    if (!layout) {
      continue
    }
    const midpointX = layout.x + layout.width / 2
    if (position.x < midpointX) {
      return index
    }
  }
  return targetColumns.length
}

/**
 * Chooses the insertion index for a task board at a board-row position.
 * @param {BoardDragPosition} position Board-row position.
 * @param {TaskBoard[]} taskBoards Ordered task boards in the target column.
 * @param {string} draggedTaskBoardId Dragged task-board id to exclude.
 * @param {Map<string, BoardItemLayout>} taskBoardLayouts Measured task-card layouts.
 * @returns {number} Zero-based insertion index.
 */
function taskBoardInsertionIndexAtPosition(
    position,
    taskBoards,
    draggedTaskBoardId,
    taskBoardLayouts
  ) {
    const targetTaskBoards = taskBoards.filter(
      (taskBoard) => taskBoard.id() !== draggedTaskBoardId
    )
  for (let index = 0; index < targetTaskBoards.length; index += 1) {
    const layout = taskBoardLayouts.get(targetTaskBoards[index].id())
    if (!layout) {
      continue
    }
    const midpointY = layout.y + layout.height / 2
    if (position.y < midpointY) {
      return index
    }
  }
  return targetTaskBoards.length
}

/**
 * Chooses the nearest measured column to a position.
 * @param {BoardDragPosition} position Board-row position.
 * @param {{column: BoardColumn, layout: BoardItemLayout | null}[]} candidates Candidate columns.
 * @returns {BoardColumn | null} Nearest column, if any column is rendered.
 */
function closestColumnForPosition(position, candidates) {
  if (candidates.length === 0) {
    return null
  }
  let nearestCandidate =
    /** @type {{column: BoardColumn, distance: number} | null} */ null
  for (const candidate of candidates) {
    if (!candidate.layout) {
      continue
    }
    const centerX = candidate.layout.x + candidate.layout.width / 2
    const centerY = candidate.layout.y + candidate.layout.height / 2
    const distance = Math.hypot(position.x - centerX, position.y - centerY)
    if (!nearestCandidate || distance < nearestCandidate.distance) {
      nearestCandidate = {
        column: candidate.column,
        distance
      }
    }
  }
  return nearestCandidate?.column || null
}
